import { useState, useEffect, useRef } from "react";
import {
  Star,
  Search,
  Filter,
  ThumbsUp,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
  Users,
  Send,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import BaseApi from "../../services/base-api";
import LoadingComponent from "../../components/LoadingComponent";
import Price from "../../components/Currency/Price";
import Pagination from "../../components/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

interface Rating {
  id: string;
  userId: string;
  productId: string;
  orderId: string;
  rating: number;
  review: string | null;
  images: string[];
  isApproved: boolean;
  adminResponse: string | null;
  adminRespondedAt: string | null;
  helpfulCount: number;
  createdAt: string;
  updatedAt: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  product: {
    id: string;
    name: {
      en: string;
      ar: string;
    };
    images: string[];
    price: number;
  };
}

type FilterStatus = "all" | "pending" | "approved" | "rejected";

const AdminRatingManagement = () => {
  const { t, i18n } = useTranslation();
  const { success, err } = useToast();
  const currentLang = i18n.language as "en" | "ar";

  const [filtered, setFiltered] = useState<Rating[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const [ratingFilter, setRatingFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const latestRequestRef = useRef(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [apiTotalItems, setApiTotalItems] = useState(0); // ADD THIS
  const [sortBy, setSortBy] = useState<"createdAt" | "rating">("createdAt"); // ADD THIS
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc"); // ADD THIS

  const [ratings, setRatings] = useState<Rating[]>([]);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [selectedRating, setSelectedRating] = useState<Rating | null>(null);
  const [adminResponse, setAdminResponse] = useState("");
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    fetchRatings();
  }, []);

  useEffect(() => {
    fetchRatings();
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    ratingFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, ratingFilter]);

  const fetchRatings = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (ratingFilter !== "all") {
        params.append("rating", ratingFilter);
      }

      const res = await BaseApi.get(`/ratings/admin/all?${params.toString()}`);
      if (requestId !== latestRequestRef.current) return;
      if (res.success) {
        setRatings(res.data || []);
        setFiltered(res.data || []);
        setApiTotalItems(res.pagination?.totalItems || res.data.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching ratings:", error);
      err(t("Failed to load ratings"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const handleApprove = async (ratingId: string) => {
    setProcessingId(ratingId);
    try {
      const res = await BaseApi.patch(`/ratings/${ratingId}/approve`);
      if (res.success) {
        success(t("Rating approved successfully"));
        fetchRatings();
      }
    } catch (error) {
      console.error("Error approving rating:", error);
      err(t("Failed to approve rating"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (ratingId: string) => {
    setProcessingId(ratingId);
    try {
      const res = await BaseApi.delete(`/ratings/${ratingId}`);
      if (res.success) {
        success(t("Rating rejected successfully"));
        fetchRatings();
      }
    } catch (error) {
      console.error("Error rejecting rating:", error);
      err(t("Failed to reject rating"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleRespond = async () => {
    if (!selectedRating || !adminResponse.trim()) return;

    setProcessingId(selectedRating.id);
    try {
      const res = await BaseApi.patch(`/ratings/${selectedRating.id}/respond`, {
        response: adminResponse,
      });
      if (res.success) {
        success(t("Response sent successfully"));
        setAdminResponse("");
        setSelectedRating(null);
        fetchRatings();
      }
    } catch (error) {
      console.error("Error responding to rating:", error);
      err(t("Failed to send response"));
    } finally {
      setProcessingId(null);
    }
  };

  const getStarColor = (rating: number, index: number) => {
    return index < rating ? "text-yellow-400" : "text-gray-300";
  };

  const filteredRatings = ratings.filter((rating) => {
    const searchLower = debouncedSearch.toLowerCase();
    const matchesSearch =
      debouncedSearch === "" ||
      rating.user?.firstName?.toLowerCase().includes(searchLower) ||
      rating.user?.lastName?.toLowerCase().includes(searchLower) ||
      rating.product?.name?.en.toLowerCase().includes(searchLower) ||
      rating.review?.toLowerCase().includes(searchLower);

    const matchesFilter =
      filterStatus === "all" ||
      (filterStatus === "pending" && !rating.isApproved) ||
      (filterStatus === "approved" && rating.isApproved) ||
      (filterStatus === "rejected" && !rating.isApproved);

    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLang, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Pagination
  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);
  const currentPageData = ratings;

  if (initialLoading) {
    return <LoadingComponent title={t("Ratings")} />;
  }

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl p-3 sm:p-4">
              <Star className="w-6 h-6 sm:w-8 sm:h-8 text-white fill-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {t("Rating Management")}
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {t("Manage product ratings and reviews")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Search by customer, product, or review...")}
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 focus:border-yellow-500 focus:outline-none appearance-none cursor-pointer text-sm"
            >
              <option value="all">{t("All Ratings")}</option>
              <option value="pending">{t("Pending")}</option>
              <option value="approved">{t("Approved")}</option>
              <option value="rejected">{t("Rejected")}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Ratings List */}
      <div
        className={
          "space-y-4" +
          (loading ? " opacity-50 pointer-events-none transition-opacity" : "")
        }
      >
        {currentPageData.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-2">
              {t("No ratings found")}
            </p>
            <p className="text-gray-400 text-sm">
              {debouncedSearch || filterStatus !== "all"
                ? t("Try adjusting your filters")
                : t("No ratings submitted yet")}
            </p>
          </div>
        ) : (
          currentPageData.map((rating) => (
            <div
              key={rating.id}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-200 hover:shadow-xl transition-all overflow-hidden"
            >
              {/* Rating Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-4 border-b-2 border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-white rounded-lg p-2 shadow-sm">
                      <Users className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">
                        {rating.user.firstName} {rating.user.lastName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {rating.user.email}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(rating.createdAt).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {!rating.isApproved &&
                      filterStatus !== "approved" &&
                      filterStatus !== "rejected" && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 border-2 border-yellow-300">
                          <Clock className="w-3 h-3" />
                          {t("Pending")}
                        </span>
                      )}
                    {rating.isApproved && filterStatus === "approved" && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border-2 border-green-300">
                        <CheckCircle className="w-3 h-3" />
                        {t("Approved")}
                      </span>
                    )}
                    {!rating.isApproved && filterStatus === "rejected" && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border-2 border-red-300">
                        <XCircle className="w-3 h-3" />
                        {t("Rejected")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Rating Content */}
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* Product Info */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Product Details */}
                    <div className="flex gap-3">
                      {rating.product.images && rating.product.images[0] && (
                        <img
                          src={rating.product.images[0]}
                          alt={rating.product.name.en}
                          className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border-2 border-gray-200"
                        />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">
                          {rating.product.name.en}
                        </p>
                        <p className="text-yellow-600 font-bold text-sm sm:text-base mt-1">
                          <Price amount={rating.product.price} />
                        </p>
                      </div>
                    </div>

                    {/* Star Rating */}
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 ${getStarColor(
                              rating.rating,
                              i,
                            )} ${i < rating.rating ? "fill-current" : ""}`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-bold text-gray-700">
                        {rating.rating}.0
                      </span>
                    </div>

                    {/* Review Text */}
                    {rating.review && (
                      <div className="bg-gray-50 rounded-xl p-4 border-l-4 border-yellow-400">
                        <p className="text-gray-900 text-sm leading-relaxed">
                          "{rating.review}"
                        </p>
                      </div>
                    )}

                    {/* Review Images */}
                    {rating.images && rating.images.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {rating.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Review ${idx + 1}`}
                            className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover border-2 border-gray-200 cursor-pointer hover:scale-105 transition-transform"
                          />
                        ))}
                      </div>
                    )}

                    {/* Helpful Count */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <ThumbsUp className="w-4 h-4" />
                      <span>
                        {rating.helpfulCount} {t("people found this helpful")}
                      </span>
                    </div>

                    {/* Admin Response */}
                    {rating.adminResponse && (
                      <div className="bg-blue-200 rounded-xl p-4 border-l-4 border-blue-400">
                        <div>
                          <h5 className="flex flex-row font-semibold text-blue-900 mb-1 gap-1">
                            <MessageSquare className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />{" "}
                            {t("Admin Response")}
                          </h5>
                          <p className="text-blue-800 text-sm">
                            {rating.adminResponse}
                          </p>
                          {rating.adminRespondedAt && (
                            <p className="text-blue-600 text-xs mt-2">
                              {formatDate(rating.adminRespondedAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex space-y-3 justify-end items-end gap-2">
                    {!rating.isApproved && (
                      <>
                        <button
                          onClick={() => handleApprove(rating.id)}
                          disabled={processingId === rating.id}
                          className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-3 rounded-lg hover:shadow-lg transition-all font-semibold text-sm disabled:opacity-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {processingId === rating.id
                            ? t("Processing...")
                            : t("Approve")}
                        </button>

                        {/* <button
                          onClick={() => handleReject(rating.id)}
                          disabled={processingId === rating.id}
                          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 rounded-lg hover:shadow-lg transition-all font-semibold text-sm disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          {processingId === rating.id
                            ? t("Processing...")
                            : t("Reject")}
                        </button> */}
                      </>
                    )}

                    <button
                      onClick={() => {
                        setSelectedRating(rating);
                        setAdminResponse(rating.adminResponse || "");
                      }}
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg hover:shadow-lg transition-all font-semibold text-sm"
                    >
                      <MessageSquare className="w-4 h-4" />
                      {rating.adminResponse ? t("Edit Response") : t("Respond")}
                    </button>

                    <button
                      onClick={() => {
                        /* View full details */
                      }}
                      className="flex items-center justify-center gap-2 border-2 border-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-50 transition-all font-semibold text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      {t("View Order")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {finalTotalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={finalTotalPages}
          totalItems={finalTotalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          showPageSizeSelector={true}
          isLoading={loading}
          compact={true}
        />
      )}

      {/* Response Modal */}
      {selectedRating && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedRating(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-gray-600 to-black text-white p-6 rounded-t-3xl">
              <h3 className="text-2xl font-bold">{t("Respond to Review")}</h3>
              <p className="text-blue-100 text-sm mt-1">
                {selectedRating.user.firstName} {selectedRating.user.lastName}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Review Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${getStarColor(
                        selectedRating.rating,
                        i,
                      )} ${i < selectedRating.rating ? "fill-current" : ""}`}
                    />
                  ))}
                </div>
                {selectedRating.review && (
                  <p className="text-gray-700 text-sm">
                    "{selectedRating.review}"
                  </p>
                )}
              </div>

              {/* Response Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Your Response")}
                </label>
                <textarea
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  rows={5}
                  placeholder={t(
                    "Write a professional response to this review...",
                  )}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#0d223f] focus:outline-none resize-none text-sm"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedRating(null)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleRespond}
                  disabled={
                    !adminResponse.trim() || processingId === selectedRating.id
                  }
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg transition-all font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {processingId === selectedRating.id
                    ? t("Sending...")
                    : t("Send Response")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRatingManagement;
